import { useCallback, useState } from "react"
import { IUser } from "@features/auth/domain/user.interface.ts"
import { LocalStorageSession } from "@/shared/utils/local-storage-session"

export function useAuth() {
  const [user, setUser] = useState<IUser | null>(() => {
    try {
      const userJson = localStorage.getItem("current_user")
      return userJson ? JSON.parse(userJson) : null
    } catch (error) {
      console.error("Error getting current user:", error)
      return null
    }
  })

  const getCurrentUser = useCallback(() => {
    try {
      const userJson = localStorage.getItem("current_user")
      if (!userJson) return null

      return JSON.parse(userJson)
    } catch (error) {
      console.error("Error getting current user:", error)
      return null
    }
  }, [])

  const login = useCallback((user: IUser, accessToken?: string) => {
    LocalStorageSession.saveAuthorization(user, accessToken)
    setUser(user)
  }, [])

  const logout = useCallback(async () => {
    await LocalStorageSession.clearSession()
    setUser(null)
  }, [])

  const hasRole = useCallback(
    (role: string) => {
      const userRoles = user?.roles || []
      const normalize = (value: string) => value.toLowerCase().replace(/[-_\s]/g, "")
      const normalizedRoles = userRoles.map(normalize)
      if (normalizedRoles.includes("admin") || normalizedRoles.includes("superadmin")) return true
      return normalizedRoles.includes(normalize(role))
    },
    [user]
  )

  const hasPermission = useCallback(
    (action: string, subject: string) => {
      if (user?.roles?.includes("admin")) return true
      return user?.permissions?.some((p) => (p.action === action || p.action === "manage") && (p.subject === subject || p.subject === "all")) ?? false
    },
    [user]
  )

  return { user, isAuthenticated: !!user, login, logout, hasRole, hasPermission, getCurrentUser }
}
